import * as React from "react";
import { VariantProps, cva } from "class-variance-authority";
import { cn } from "@/common/utils/cn";
import { mergeRefs } from "@/common/utils/mergeRefs";
const inputClassName = "peer grow w-full autofill:shadow-[inset_0_0_0px_9999px_rgb(247,247,248)] appearance-none bg-gray-50 outline-none file:border-0 file:bg-gray-50 file:text-sm file:font-medium disabled:cursor-not-allowed";
const inputWrapperClassName = "relative mb-5 rounded-md bg-gray-50 flex items-center justify-between gap-x-1 border-solid cursor-text aria-disabled:cursor-not-allowed";
const inputWrapperVariants = cva(inputWrapperClassName, {
  variants: {
    variant: {
      bordered: "border",
      ghost: "border-none"
    },
    size: {
      sm: "h-9 px-3 py-[10px] text-xs",
      md: "h-10 px-3 py-[10px] text-xs",
      lg: "h-12 p-3 text-sm",
      xl: "h-14 px-3 py-4 text-sm"
    },
    color: {
      error: "border-error fill-success text-error",
      success: "border-success fill-success text-success",
      gray: "border-gray-200 fill-gray-500 text-gray-500 focus-within:border-foreground focus-within:fill-foreground focus-within:text-foreground"
    },
    disabled: {
      true: "fill-gray-300 text-gray-300"
    },
    fullWidth: {
      true: "w-full"
    }
  },
  defaultVariants: {
    size: "lg",
    color: "gray",
    variant: "bordered"
  }
});
interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "color" | "disabled">, VariantProps<typeof inputWrapperVariants> {
  label?: string;
  message?: string;
  noMessage?: boolean;
  endContent?: React.ReactNode;
  startContent?: React.ReactNode;
  messageDataTestId?: string;
}
const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  type,
  size,
  label,
  color,
  variant,
  message,
  className,
  noMessage,
  fullWidth,
  endContent,
  startContent,
  placeholder = " ",
  messageDataTestId = "",
  ...props
}, ref) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const mergedRefs = React.useMemo(() => mergeRefs(ref, inputRef), [ref]);
  const startContentContainerRef = React.useRef<HTMLDivElement>(null);
  return <div aria-disabled={!!props.disabled} onClick={() => inputRef.current?.focus()} className={cn(inputWrapperVariants({
    size,
    color,
    variant,
    fullWidth,
    disabled: !!props.disabled
  }), {
    "mb-0": noMessage
  }, className)}>
        {!!startContent && <div ref={startContentContainerRef} className="flex h-full shrink-0 items-center justify-center">
            {startContent}
          </div>}
        <input type={type} ref={mergedRefs} className={cn("text-base placeholder:text-sm placeholder:font-normal", inputClassName, {
      "text-gray-300 placeholder:text-gray-300": props.disabled,
      "text-foreground placeholder:text-gray-500": !props.disabled,
      "placeholder:text-error": color === "error"
    })} {...props} disabled={!!props.disabled} placeholder={!!label ? " " : placeholder} />
        {!!endContent && <div className="flex h-full shrink-0 items-center justify-center">
            {endContent}
          </div>}
        {!!label && <span style={{
      "--content-offset": `-${startContentContainerRef.current?.clientWidth ?? 0}px`
    } as React.CSSProperties} className={cn("pointer-events-none absolute origin-top-right bg-inherit px-1 rounded transition-[transform,colors] duration-300 scale-75 transform-gpu peer-placeholder-shown:bg-transparent peer-placeholder-shown:translate-x-[calc(var(--content-offset)+.25rem)] peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:bg-inherit peer-focus:translate-x-0 peer-focus:scale-75", {
      sm: "translate-y-[calc(-100%-0.1rem)] peer-focus:translate-y-[calc(-100%-0.1rem)]",
      md: "translate-y-[calc(-100%-0.1rem)] peer-focus:translate-y-[calc(-100%-0.1rem)]",
      lg: "translate-y-[calc(-100%-0.2rem)] peer-focus:translate-y-[calc(-100%-0.2rem)]",
      xl: "translate-y-[calc(-100%-0.375rem)] peer-focus:translate-y-[calc(-100%-0.375rem)]"
    }[size || "lg"])}>
            {label}
          </span>}
        {!noMessage && <p data-testid={messageDataTestId} className="absolute right-0 top-[calc(100%+.30rem)] text-xs font-normal">
            {message}
          </p>}
      </div>;
});
Input.displayName = "Input";
export { Input };